<script setup>
import { ref, computed, onMounted } from "vue";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useRoute } from "vue-router";
import { useAcademyStore, useListStore, useUserStore } from "@/stores";

import {
  BaseKit,
  Bold,
  Color,
  Fullscreen,
  Heading,
  Highlight,
  History,
  Image,
  Italic,
  Link,
  Strike,
  Table,
  Underline,
  Video,
  VuetifyTiptap,
} from "vuetify-pro-tiptap";
import "vuetify-pro-tiptap/style.css";

const extensions = [
  BaseKit.configure({
    placeholder: {
      placeholder: "Enter some text...",
    },
  }),
  Bold,
  Italic,
  Underline,
  Strike,
  Color,
  Highlight,
  Heading,
  Link,
  Image,
  Video,
  Table,
  Fullscreen,
  History,
];

const route = useRoute();
const userStore = useUserStore();
const listStore = useListStore();
const academyStore = useAcademyStore();

const user = userStore.user;

const courses = computed(() => listStore.courses);

const submitForm = async () => {
  const isValid = await v$.value.$validate();
  form.value.createdUserId = user.id;

  const data = form.value;

  if (isValid) {
    academyStore.createCourseEvaluation(data);
  }
};

const rules = {
  evaluationId: {
    required: helpers.withMessage("Student ID is required", required),
  },
  course: {
    required: helpers.withMessage("Course is required", required),
  },
  useful: {
    required: helpers.withMessage("Question is required", required),
  },
  helpful: {
    required: helpers.withMessage("Question is required", required),
  },
  teacherClear: {
    required: helpers.withMessage("Question is required", required),
  },
  fun: {
    required: helpers.withMessage("Question is required", required),
  },
  goodTraining: {
    required: helpers.withMessage("Question is required", required),
  },
  improvement: {
    required: helpers.withMessage("Question is required", required),
  },
  likedMore: {
    required: helpers.withMessage("Question is required", required),
  },
  team: {
    required: helpers.withMessage("Question is required", required),
  },
  refer: {
    required: helpers.withMessage("Question is required", required),
  },
};

const form = ref({
  evaluationId: null,
  course: null,
  useful: null,
  helpful: null,
  prepared: null,
  skillSet: null,
  teacherClear: null,
  notBiased: null,
  fun: null,
  comfort: null,
  trainingClear: null,
  trainingClearSpecify: null,
  goodTraining: null,
  improvement: null,
  likedMore: null,
  team: null,
  refer: null,
  confident: null,
  teamwork: null,
  strength: null,
  weakness: null,
  expectation: null,
});

const evaluation = ref({});

const v$ = useVuelidate(rules, form);

const isRequired = (field) => {
  if (rules[field] != null) {
    return "required" in rules[field];
  }
  return false;
};

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$dirty && v$.value[field].$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

// This returns the index (1–N) of the selected ID in the options
function getSliderIndex(question) {
  const selectedId = form.value[question.key];
  const index = question.options.findIndex((opt) => opt.id === selectedId);
  return index !== -1 ? index + 1 : null;
}

// When slider changes, set the selected option's ID in the form
function setSliderId(question, index) {
  const option = question.options[index - 1];
  if (option) {
    form.value[question.key] = option.id;
  }
}

onMounted(async () => {
  if (route.name === "tutor-evaluation") {
    const data = await academyStore.getEvaluation("tutor");
    evaluation.value = data;
  }
  if (route.name === "student-evaluation") {
    const data = await academyStore.getEvaluation("student");
    evaluation.value = data;
  }
  await listStore.fetchCourses();
});
</script>

<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="6">
        <h1 class="text-academy">
          Add Course Evaluation
        </h1>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        class="d-flex justify-start justify-sm-end align-center"
      >
        <v-btn
          color="academy"
          size="large"
          @click="submitForm"
        >
          <v-icon icon="mdi-plus" /> Create course evaluation
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-form class="custom-form mt-5 d-flex flex-column">
          <v-text-field
            v-model="form.evaluationId"
            name="id"
            label="Evaluation(ID)"
            hide-details="auto"
            :error-messages="getErrorMessages('evaluationId').value"
            @blur="v$.evaluationId.$touch()"
          >
            <template #label>
              Evaluation (ID)
              <span
                v-if="isRequired('evaluationId')"
                class="ml-1 red--text"
              >
                <strong> *</strong>
              </span>
            </template>
          </v-text-field>

          <Autocomplete
            v-model="form.course"
            label="Evaluated Course"
            :items="courses"
            :multiple="false"
            :chips="false"
            :blur-check="() => v$.course.$touch()"
            :error-messages="getErrorMessages('course').value"
            :is-required="isRequired('course')"
          />

          <div
            v-for="question in evaluation.questions"
            :key="question.id"
          >
            <p
              v-if="question.type === 'SCALE' && question.id != 17"
              class="font-weight-bold"
            >
              {{ question.name }}
              <span
                v-if="isRequired(question.key)"
                class="ml-1 red--text"
              >
                <strong> *</strong>
              </span>
            </p>
            <v-input
              v-if="question.type === 'SCALE' && question.id != 17"
              :error-messages="getErrorMessages(question.key).value"
              @blur="v$[question.key].$touch()"
            >
              <v-rating
                :model-value="getSliderIndex(question)"
                empty-icon="mdi-circle-outline"
                :item-labels="['Low', '', '', '', 'High']"
                item-label-position="bottom"
                color="academy"
                full-icon="mdi-circle"
                :length="question.numOfOptions"
                hover
                @update:model-value="(value) => setSliderId(question, value)"
              />
            </v-input>
            <v-radio-group
              v-if="question.type === 'CHOICE'"
              v-model="form[question.key]"
              class="custom-radio-group"
              color="academy"
              hide-details="auto"
              inline
            >
              <v-radio
                v-for="option in question.options"
                :key="option.id"
                :value="option.id"
                :label="option.name"
              />
              <template #label>
                <span class="font-weight-bold">{{ question.name }}</span>
              </template>
            </v-radio-group>

            <v-input
              v-if="question.type === 'TEXT'"
              :error-messages="getErrorMessages(question.key).value"
              class="input-wrapper"
              @update:model-value="(value) => setSliderId(question, value)"
              @blur="v$[question.key].$touch()"
            >
              <div class="font-weight-bold">
                {{ question.name }}
                <span
                  v-if="isRequired(question.key)"
                  class="ml-1 red--text"
                >
                  <strong> *</strong>
                </span>
              </div>
              <VuetifyTiptap
                v-if="question.type === 'TEXT'"
                v-model="form[question.key]"
                rounded
                :min-height="200"
                :extensions="extensions"
              />
            </v-input>
          </div>
        </v-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-academy));
}
.flex-50 {
  flex: 0 0 50%;
}
.vuetify-pro-tiptap {
  width: 100%;
}
</style>
